<template>
  <v-row
    no-gutters
    class="feature-display-layout rounded-lg overflow-hidden mx-0"
  >
    <v-col cols="12" md="4" class="feature-display-hero position-relative">
      <v-img
        v-if="heroImage"
        :src="heroImage"
        height="100%"
        min-height="320"
        cover
        class="feature-display-hero-img"
      />
      <div
        v-else
        class="feature-display-hero-fallback d-flex align-center justify-center fill-height"
      >
        <v-icon size="96" color="grey darken-1">
          {{ feature.icon || "mdi-star" }}
        </v-icon>
      </div>
      <v-chip
        v-if="feature.status"
        small
        label
        dark
        :color="statusMeta.color"
        class="feature-display-status-badge"
      >
        {{ statusMeta.text }}
      </v-chip>
    </v-col>

    <v-col cols="12" md="8" class="pa-6 white feature-display-content">
      <div
        v-if="pathLabel"
        class="text-uppercase caption primary--text font-weight-medium mb-1"
      >
        <v-icon x-small color="primary" class="mr-1">
          {{ feature.icon || "mdi-chevron-double-right" }}
        </v-icon>
        {{ pathLabel }}
      </div>

      <h2 class="text-h5 font-weight-bold mb-1">
        {{ feature.title || "Título da funcionalidade" }}
      </h2>
      <div v-if="feature.subtitle" class="text-subtitle-1 lh-5 mb-4">
        {{ feature.subtitle }}
      </div>

      <p
        v-if="feature.description"
        class="text-body-2 mb-6"
        style="white-space: pre-line"
      >
        {{ feature.description }}
      </p>

      <div v-if="benefitItems.length" class="mb-6">
        <div class="text-overline grey--text mb-3 d-flex align-center">
          <v-icon class="mr-1">mdi-lightning-bolt</v-icon>
          Principais benefícios
        </div>
        <v-alert
          v-for="(benefit, index) in benefitItems"
          :key="`benefit-${index}`"
          text
          class="mb-3 rounded-lg"
        >
          <div class="d-flex align-start gap-2">
            <v-icon class="mr-1" color="success"
              >mdi-check-circle-outline</v-icon
            >
            <div>
              <div class="font-weight-bold mb-1">
                {{ benefit.title }}
              </div>
              <div class="text-body-2">{{ benefit.description }}</div>
            </div>
          </div>
        </v-alert>
      </div>

      <div v-if="faqItems.length" class="mb-6">
        <div class="text-overline grey--text mb-3">Dúvidas frequentes</div>
        <v-card
          v-for="(item, index) in faqItems"
          :key="`faq-${index}`"
          outlined
          class="mb-3 pa-4 py-3"
          rounded="lg"
        >
          <b class="text-subtitle-2 font-weight-bold py-3">
            {{ item.question }}
          </b>
          <p class="mb-0 text-body-2">
            {{ item.answer }}
          </p>
        </v-card>
      </div>

      <div v-if="screenshotItems.length" class="mb-6">
        <div class="text-overline grey--text mb-3">Screenshots</div>
        <v-row dense>
          <v-col
            v-for="(shot, index) in screenshotItems"
            :key="`shot-${index}`"
            cols="12"
            sm="6"
          >
            <v-img
              :src="shot"
              max-height="220"
              contain
              class="rounded-lg grey lighten-4"
            />
          </v-col>
        </v-row>
      </div>

      <div v-if="tagItems.length" class="mb-6">
        <div class="text-overline grey--text mb-3 d-flex align-center">
          <v-icon small class="mr-1">mdi-tag-outline</v-icon>
          Tags
        </div>
        <v-chip
          v-for="(tag, index) in tagItems"
          :key="`tag-${index}`"
          small
          label
          class="mr-2 mb-2 feature-display-tag"
        >
          #{{ tag }}
        </v-chip>
      </div>

      <div v-if="audienceItems.length" class="mb-6">
        <div class="text-overline grey--text mb-3 d-flex align-center">
          <v-icon small class="mr-1">mdi-account-group</v-icon>
          Público-alvo
        </div>
        <v-chip
          v-for="audience in audienceItems"
          :key="audience.id || audience.name"
          small
          label
          class="mr-2 mb-2"
        >
          {{ audience.name }}
        </v-chip>
      </div>

      <v-btn
        v-if="feature.ctaLabel"
        block
        color="primary darken-3"
        class="mb-4 text-none"
        :href="feature.ctaUrl || undefined"
        :target="feature.ctaUrl ? '_blank' : undefined"
        :disabled="!feature.ctaUrl"
      >
        {{ feature.ctaLabel }}
        <v-icon right small>mdi-open-in-new</v-icon>
      </v-btn>

      <div v-if="linkItems.length" class="d-flex flex-wrap justify-center">
        <v-btn
          v-for="(link, index) in linkItems"
          :key="`${link.label}-${index}`"
          text
          small
          color="grey darken-1"
          class="text-none mr-2"
          :href="link.url"
          target="_blank"
        >
          <v-icon left small>{{ linkIcon(link.label) }}</v-icon>
          {{ link.label }}
        </v-btn>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { FEATURE_STATUSES, formatPath } from "@/utils/featureUtils";

export default {
  name: "FeatureDisplayBody",
  props: {
    feature: { type: Object, default: () => ({}) },
  },
  computed: {
    statusMeta() {
      return (
        FEATURE_STATUSES[this.feature.status] || FEATURE_STATUSES.AVAILABLE
      );
    },
    pathLabel() {
      return formatPath(this.feature.path);
    },
    heroImage() {
      return this.screenshotItems[0] || "";
    },
    benefitItems() {
      return (this.feature.mainBenefits || []).filter(
        (item) => item?.title?.trim() && item?.description?.trim()
      );
    },
    faqItems() {
      return (this.feature.faq || []).filter(
        (item) => item?.question?.trim() && item?.answer?.trim()
      );
    },
    screenshotItems() {
      return (this.feature.screenshots || []).filter((url) => url?.trim());
    },
    tagItems() {
      return (this.feature.categories || []).filter((tag) => tag?.trim());
    },
    audienceItems() {
      if (this.feature.audiences?.length) return this.feature.audiences;
      return (this.feature.audienceItems || []).filter(Boolean);
    },
    linkItems() {
      return (this.feature.links || []).filter(
        (item) => item?.label?.trim() && item?.url?.trim()
      );
    },
  },
  methods: {
    linkIcon(label = "") {
      const lower = label.toLowerCase();
      if (lower.includes("vídeo") || lower.includes("video"))
        return "mdi-play-circle-outline";
      if (lower.includes("doc")) return "mdi-file-document-outline";
      return "mdi-link-variant";
    },
  },
};
</script>

<style scoped>
.feature-display-layout {
  border: 1px solid #e5e7eb;
}

.feature-display-hero {
  min-height: 320px;
  background: #f3f4f6;
}

.feature-display-hero-fallback {
  min-height: 320px;
}

.feature-display-status-badge {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 1;
}

.feature-display-tag {
  background: #ecfdf5 !important;
  color: #047857 !important;
}

.feature-display-content {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
